import React, { FC, useCallback, useState } from 'react'
import { useCreateSection75Dispute } from '../../hooks'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Button, Heading, Icon, Modal, Text, Checkbox, useMoney, Divider } from '@afterpaytouch/core'
import { useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { DisputeItemDetails } from './DisputeItemDetails'

const I18N_NAMESPACE = ['dispute']

export const Review: FC = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { textSize } = useConsumerSizes()
  const router = useRouter()
  const { selectedItems, files, setFiles, useCustomAmount, customAmount, setStep, step } = useSection75DisputeModalContext()
  const createDispute = useCreateSection75Dispute()
  const [isAgreed, setIsAgreed] = useState(false)

  const backHandler = useCallback(() => {
    setStep(step - 1)
  }, [setStep, step])

  const handleSubmit = useCallback(() => {
    if (isAgreed) {
      createDispute()
    }
  }, [isAgreed, createDispute])

  const handleAgreementChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(event.target.checked)
  }, [])

  const handleDeleteFile = useCallback(
    (index: number) => {
      setFiles(files.filter((_, i) => i !== index))
    },
    [files, setFiles]
  )

  const { formatMoney } = useMoney({
    currency: (selectedItems[0]?.amount?.currency || 'GBP') as any,
    locale: router.locale as any,
  })

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:review:heading')}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='space-y-6'>
          {/* Description */}
          <div>
            <Text size={textSize}>{t('dispute:section75DisputeModal:review:description')}</Text>
          </div>

          {/* Dispute Details Section */}
          <div>
            <Heading size='M'>{t('dispute:section75DisputeModal:review:disputeDetailsHeading')}</Heading>

            {selectedItems.map((item, index) => {
              const displayAmount = useCustomAmount && customAmount > 0 ? formatMoney(customAmount.toString()) : formatMoney(item.amount?.amount ?? '0.00')

              return (
                <div key={item.key || index}>
                  <DisputeItemDetails
                    itemName={item.name}
                    disputeReason={item.reason ?? ''}
                    disputeDescription={item.description ?? ''}
                    disputeAmount={displayAmount}
                  />
                  {index < selectedItems.length - 1 && <Divider kind='Dashed' />}
                </div>
              )
            })}
          </div>

          {/* Dotted Divider */}
          <Divider kind='Dashed' />

          {/* Supporting Documents Section */}
          <div className='space-y-4'>
            <Heading size='M'>{t('dispute:section75DisputeModal:review:supportingDocsHeading')}</Heading>

            <div className='space-y-3'>
              {files.length > 0 &&
                files.map((file, index) => (
                  <div key={index} className='flex items-center justify-between'>
                    <Text size={textSize}>{file.name}</Text>
                    <Button.Link onClick={() => handleDeleteFile(index)}>
                      <div>
                        <Icon.Close size='Small' />
                      </div>
                    </Button.Link>
                  </div>
                ))}
            </div>
          </div>

          {/* Agreement Checkbox */}
          <div className='space-y-4'>
            <Checkbox
              id='agreement-checkbox'
              checked={isAgreed}
              onChange={handleAgreementChange}
              label={<Text size={textSize}>{t('dispute:section75DisputeModal:review:disclaimer')}</Text>}
              size='Medium'
            />
          </div>

          {/* Submit Button */}
          <div>
            <Button padding='Fluid' size='Large' onClick={handleSubmit} disabled={!isAgreed}>
              {t('dispute:section75DisputeModal:review:submit')}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </>
  )
}
